import type { ReactNode } from 'react';
import type { LucideIcon } from 'lucide-react';
import { ArrowLeft, ChevronLeft, ChevronRight } from 'lucide-react';
import { Button } from '@/components/atoms/button';
import { Skeleton } from '@/components/atoms/skeleton';
import { SearchInput } from '@/components/molecules/SearchInput';
import { BusinessUnitScopeStep } from '@/components/organisms/business-units/BusinessUnitScopeStep';
import { cn } from '@/lib/utils';

interface PickerOption {
  title: string;
  description: string;
  onClick: () => void;
  icon: LucideIcon;
  badge?: string;
  emphasis?: 'primary' | 'default';
}

interface TemplateOption {
  id: string;
  title: string;
  description: string;
  onClick: () => void;
  icon?: LucideIcon;
}

interface TemplatePagination {
  page: number;
  totalPages: number;
  total: number;
  onPageChange: (page: number) => void;
}

interface TemplateLoadError {
  message: string;
  onRetry: () => void;
}

interface BusinessUnitStep {
  value: string | null;
  onChange: (value: string | null) => void;
  /** What the choice narrows, e.g. "templates" or "job types". */
  narrows?: string;
  /**
   * The flow will not go on without a unit. While `value` is null every choice
   * below the step — scratch options, templates, the extra action — is inert,
   * and the template list is replaced by a line saying what to do. An optional
   * step (the default) gates nothing: an empty selection just filters nothing.
   */
  required?: boolean;
}

interface CreateFromTemplatePickerProps {
  title: string;
  description: string;
  onBack: () => void;
  /**
   * Optional up-front "which business unit?" choice, shown above the options it
   * narrows. It renders nothing when the tenant has no active business units,
   * and an empty selection filters nothing — so passing it is free. Mark it
   * `required` to hold everything below it closed until a unit is chosen.
   */
  businessUnitStep?: BusinessUnitStep;
  scratchTitle: string;
  scratchDescription: string;
  scratchIcon: LucideIcon;
  scratchOptions: PickerOption[];
  /** Optional specialized chooser rendered inside the scratch card. */
  scratchContent?: ReactNode;
  /** Gives dense side-by-side creation choices a little more horizontal room. */
  wide?: boolean;
  templateTitle: string;
  templateDescription: string;
  templateIcon: LucideIcon;
  isLoadingTemplates: boolean;
  templateEmptyMessage: string;
  templateOptions: TemplateOption[];
  /** Optional controlled search for template-backed pickers. */
  templateSearchValue?: string;
  onTemplateSearchChange?: (value: string) => void;
  templateSearchPlaceholder?: string;
  templateNoResultsMessage?: string;
  templatePageEmptyMessage?: string;
  isSearchingTemplates?: boolean;
  templatePagination?: TemplatePagination;
  templateLoadError?: TemplateLoadError;
  /** When true, the "From Template" section is not rendered (e.g. pickers that only offer scratch options). */
  hideTemplateSection?: boolean;
  extraAction?: PickerOption;
}

/**
 * A card that goes quiet until the business unit is chosen.
 *
 * `inert` (React 19) is the load-bearing part: it removes the subtree from the
 * tab order and the accessibility tree and swallows clicks, so a keyboard user
 * cannot reach a choice a mouse user cannot see as available. `opacity` alone
 * would only LOOK closed.
 */
function GatedCard({ gated, children }: { gated: boolean; children: ReactNode }) {
  return (
    <div
      inert={gated || undefined}
      className={cn(
        'transition-opacity duration-200',
        gated && 'pointer-events-none select-none opacity-40',
      )}
    >
      {children}
    </div>
  );
}

export function CreateFromTemplatePicker({
  title,
  description,
  onBack,
  businessUnitStep,
  scratchTitle,
  scratchDescription,
  scratchIcon: ScratchIcon,
  scratchOptions,
  scratchContent,
  wide = false,
  templateTitle,
  templateDescription,
  templateIcon: TemplateIcon,
  isLoadingTemplates,
  templateEmptyMessage,
  templateOptions,
  templateSearchValue,
  onTemplateSearchChange,
  templateSearchPlaceholder = 'Search templates...',
  templateNoResultsMessage = 'No matching templates found.',
  templatePageEmptyMessage = 'No templates on this page. Use the pagination controls to go back.',
  isSearchingTemplates = false,
  templatePagination,
  templateLoadError,
  hideTemplateSection,
  extraAction,
}: CreateFromTemplatePickerProps) {
  // Closed only by a REQUIRED step with nothing chosen. An optional step never
  // gates, and a flow that passes no step (tenant without units) never gates.
  const gateClosed = businessUnitStep?.required === true && businessUnitStep.value === null;
  return (
    <div className="flex h-full flex-col overflow-y-auto">
      <div
        className={cn(
          'mx-auto my-auto w-full space-y-5 p-4 pb-10 sm:p-6',
          wide ? 'max-w-3xl' : 'max-w-2xl',
        )}
      >
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon-sm" aria-label="Back" onClick={onBack}>
            <ArrowLeft className="size-4" />
          </Button>
          <div>
            <h1 className="text-lg font-bold sm:text-xl">{title}</h1>
            <p className="text-sm text-muted-foreground">{description}</p>
          </div>
        </div>

        {businessUnitStep ? (
          <BusinessUnitScopeStep
            value={businessUnitStep.value}
            onChange={businessUnitStep.onChange}
            narrows={businessUnitStep.narrows}
            required={businessUnitStep.required}
          />
        ) : null}

        <GatedCard gated={gateClosed}>
          {scratchContent ? (
            scratchContent
          ) : scratchOptions.length === 1 ? (
            <button
              className="w-full overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-colors hover:bg-muted/30"
              onClick={scratchOptions[0].onClick}
            >
              <div className="flex items-center gap-3 p-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  {(() => {
                    const Icon = scratchOptions[0].icon;
                    return <Icon className="h-4 w-4 text-primary" />;
                  })()}
                </div>
                <div className="flex-1">
                  <p className="text-sm font-semibold">{scratchOptions[0].title}</p>
                  <p className="text-xs text-muted-foreground">{scratchOptions[0].description}</p>
                </div>
              </div>
            </button>
          ) : (
            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              <div className="flex items-center gap-3 px-4 pb-3 pt-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
                  <ScratchIcon className="h-4 w-4 text-primary" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{scratchTitle}</p>
                  <p className="text-xs text-muted-foreground">{scratchDescription}</p>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3 px-4 pb-4">
                {scratchOptions.map((option) => {
                  const OptionIcon = option.icon;
                  const primary = option.emphasis === 'primary';
                  return (
                    <button
                      key={option.title}
                      className={`relative flex items-start gap-3 rounded-lg p-3 text-left transition-colors ${
                        primary
                          ? 'border-2 border-primary/30 bg-primary/5 hover:bg-primary/10'
                          : 'border border-border bg-card hover:bg-muted/50'
                      }`}
                      onClick={option.onClick}
                    >
                      {option.badge && (
                        <span className="absolute right-2 top-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-[10px] font-bold uppercase tracking-wide text-primary">
                          {option.badge}
                        </span>
                      )}
                      <OptionIcon
                        className={`h-5 w-5 ${primary ? 'text-primary' : 'text-muted-foreground'}`}
                      />
                      <div className="min-w-0 flex-1">
                        <p className="pr-12 text-sm font-semibold">{option.title}</p>
                        <p className="text-xs text-muted-foreground">{option.description}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </GatedCard>

        {!hideTemplateSection && (
          <GatedCard gated={gateClosed}>
            <div className="overflow-hidden rounded-xl border bg-card shadow-sm">
              <div className="flex items-center gap-3 px-4 pb-3 pt-4">
                <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-muted">
                  <TemplateIcon className="h-4 w-4 text-muted-foreground" />
                </div>
                <div>
                  <p className="text-sm font-semibold">{templateTitle}</p>
                  <p className="text-xs text-muted-foreground">{templateDescription}</p>
                </div>
              </div>

              {gateClosed && (
                <p className="px-4 pb-4 text-xs text-muted-foreground">
                  Choose a business unit to see its templates.
                </p>
              )}

              {!gateClosed && templateSearchValue !== undefined && onTemplateSearchChange && (
                <div className="px-4 pb-3">
                  <SearchInput
                    value={templateSearchValue}
                    onChange={(event) => onTemplateSearchChange(event.target.value)}
                    placeholder={templateSearchPlaceholder}
                    aria-label={templateSearchPlaceholder}
                    loading={isSearchingTemplates}
                  />
                </div>
              )}

              {!gateClosed && isLoadingTemplates && (
                <div className="space-y-2 px-4 pb-4">
                  {Array.from({ length: 2 }).map((_, index) => (
                    <Skeleton key={index} className="h-14 w-full rounded-lg" />
                  ))}
                </div>
              )}

              {!gateClosed && !isLoadingTemplates && templateLoadError && (
                <div className="flex items-center justify-between gap-3 px-4 pb-4">
                  <p className="text-xs text-muted-foreground">{templateLoadError.message}</p>
                  <Button
                    type="button"
                    variant="outline"
                    size="xs"
                    onClick={templateLoadError.onRetry}
                  >
                    Retry
                  </Button>
                </div>
              )}

              {!gateClosed &&
                !isLoadingTemplates &&
                !templateLoadError &&
                templateOptions.length === 0 && (
                  <p className="px-4 pb-4 text-xs text-muted-foreground">
                    {templatePagination && templatePagination.total > 0
                      ? templatePageEmptyMessage
                      : templateSearchValue?.trim()
                        ? templateNoResultsMessage
                        : templateEmptyMessage}
                  </p>
                )}

              {!gateClosed && !isLoadingTemplates && templateOptions.length > 0 && (
                <div className="space-y-2 px-4 pb-4">
                  {templateOptions.map((option) => {
                    const OptionIcon = option.icon ?? TemplateIcon;
                    return (
                      <button
                        key={option.id}
                        className="flex w-full items-center gap-3 rounded-lg border border-border bg-background p-3 text-left transition-colors hover:bg-muted/50"
                        onClick={option.onClick}
                      >
                        <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-muted">
                          <OptionIcon className="h-4 w-4 text-muted-foreground" />
                        </div>
                        <div className="min-w-0 flex-1">
                          <p className="truncate text-sm font-medium">{option.title}</p>
                          <p className="text-xs text-muted-foreground">{option.description}</p>
                        </div>
                        <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                      </button>
                    );
                  })}
                </div>
              )}

              {!gateClosed &&
                !isLoadingTemplates &&
                (!templateLoadError || templateOptions.length > 0) &&
                templatePagination &&
                (templatePagination.totalPages > 1 || templatePagination.page > 1) && (
                  <div className="flex items-center justify-between gap-3 border-t px-4 py-3">
                    <p className="text-xs text-muted-foreground">
                      Page {templatePagination.page} of {templatePagination.totalPages} ·{' '}
                      {templatePagination.total} templates
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Previous template page"
                        disabled={templatePagination.page <= 1}
                        onClick={() => templatePagination.onPageChange(templatePagination.page - 1)}
                      >
                        <ChevronLeft />
                      </Button>
                      <Button
                        type="button"
                        variant="ghost"
                        size="icon-xs"
                        aria-label="Next template page"
                        disabled={templatePagination.page >= templatePagination.totalPages}
                        onClick={() => templatePagination.onPageChange(templatePagination.page + 1)}
                      >
                        <ChevronRight />
                      </Button>
                    </div>
                  </div>
                )}
            </div>
          </GatedCard>
        )}

        {extraAction &&
          (() => {
            const ExtraIcon = extraAction.icon;
            return (
              <GatedCard gated={gateClosed}>
                <button
                  className="w-full overflow-hidden rounded-xl border bg-card text-left shadow-sm transition-colors hover:bg-muted/30"
                  onClick={extraAction.onClick}
                >
                  <div className="flex items-center gap-3 p-4">
                    <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-brand-blue/10">
                      <ExtraIcon
                        className="h-4 w-4 text-brand-blue"
                        style={{ color: 'var(--brand-blue)' }}
                      />
                    </div>
                    <div className="flex-1">
                      <p className="text-sm font-semibold">{extraAction.title}</p>
                      <p className="text-xs text-muted-foreground">{extraAction.description}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
                  </div>
                </button>
              </GatedCard>
            );
          })()}
      </div>
    </div>
  );
}
